import {
  Input,
  Table,
  Tag,
  Tabs,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col,
  Button,
  Popover,
  Checkbox,
  Space,
  Typography,
  Modal,
  InputNumber,
  message,
} from "antd";
import type { TableProps } from "antd";
import { ColumnProps } from "antd/lib/table";
import React, { Fragment, useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import { Bar } from "react-chartjs-2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  DownloadOutlined,
  EyeOutlined,
  OrderedListOutlined,
  SyncOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { formatDateWithTimezoneOffset } from "../../lib/lib";
import StableDatePicker from "../common/StableDatePicker";
const { Search } = Input;

Chart.register(CategoryScale);

const LS_MEMBER_HIDDEN_HOURS = "kudos-public-time-tracking:hiddenMembers:hours";
const LS_MEMBER_HIDDEN_LATE_DAILY = "kudos-public-time-tracking:hiddenMembers:lateDaily";

function loadHiddenMembersFromStorage(storageKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x) => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function saveHiddenMembersToStorage(storageKey: string, hidden: Set<string>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(Array.from(hidden)));
  } catch {
    /* ignore */
  }
}

// Strip diacritics để match fuzzy giữa server order và userName.
const stripDiacritics = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");

const fuzzyMemberKey = (name: string): string =>
  stripDiacritics(name).trim().toLowerCase().replace(/\s+/g, " ");

const buildFuzzyPositionMap = (names: string[]): Map<string, number> =>
  new Map(names.map((n, i) => [fuzzyMemberKey(n), i]));

const MEMBER_ORDER_ENDPOINT = "/api/public/time-tracking/member-order";
const WORK_SESSION_USERS_ENDPOINT = "/api/public/time-tracking/users";

interface WorkSessionUser {
  id: string;
  name: string;
  status: string;
}

type ColumnSortOrder = "ascend" | "descend" | null;

interface TimeTrackingSession {
  sessionId: string;
  startTime: string;
  endTime: string | null;
  durationSeconds: number | null;
  durationFormatted: string;
  shift?: number;
  shiftName?: string;
  onboardSeconds?: number;
  otSeconds?: number;
  onboardFormatted?: string;
  otFormatted?: string;
  isLongSession: boolean;
  isLateDaily?: boolean;
  isActive?: boolean;
}

interface UserTimeTracking {
  userId: string;
  userName: string;
  sessions: TimeTrackingSession[];
  totalDuration: number;
  totalDurationFormatted: string;
  totalShifts?: number;
  onboardDuration?: number;
  otDuration?: number;
  onboardDurationFormatted?: string;
  otDurationFormatted?: string;
  totalSessions: number;
  longSessions: number;
  lateDailySessions?: number;
  averageDuration: number;
  averageDurationFormatted: string;
  isActive?: boolean;
}

interface DailySessionGroup {
  dayKey: string;
  dayLabel: string;
  sessions: TimeTrackingSession[];
  totalDuration: number;
  onboardDuration: number;
  otDuration: number;
  longSessions: number;
  lateDailySessions: number;
}

type LateDailyRow = UserTimeTracking & {
  lateDailySessions: number;
  lateSessions: TimeTrackingSession[];
};

/** Title-case tên riêng tiếng Việt: mỗi từ viết hoa chữ đầu. */
function titleCaseName(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Tạo placeholder cho user có trong server order nhưng chưa có entry. */
function makePlaceholderUser(userName: string): UserTimeTracking {
  return {
    userId: "",
    userName: titleCaseName(userName),
    sessions: [],
    totalDuration: 0,
    totalDurationFormatted: "0h 0m",
    totalShifts: 0,
    onboardDuration: 0,
    otDuration: 0,
    onboardDurationFormatted: "0h 0m",
    otDurationFormatted: "0h 0m",
    totalSessions: 0,
    longSessions: 0,
    lateDailySessions: 0,
    averageDuration: 0,
    averageDurationFormatted: "0h 0m",
    isActive: false,
  };
}

/** Sort theo Tổng giờ làm (totalDuration) hoặc Onboard (onboardDuration). Cột khác không sortable. */
function applyHoursColumnSort(
  rows: UserTimeTracking[],
  columnKey: string | null,
  order: ColumnSortOrder
): UserTimeTracking[] {
  if (!columnKey || !order) return [...rows];
  const dir = order === "ascend" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (columnKey === "totalDuration") {
      cmp = a.totalDuration - b.totalDuration;
    } else if (columnKey === "onboardDuration") {
      cmp = (a.onboardDuration ?? 0) - (b.onboardDuration ?? 0);
    }
    return dir * cmp;
  });
}

function applyLateDailyColumnSort(
  rows: LateDailyRow[],
  columnKey: string | null,
  order: ColumnSortOrder
): LateDailyRow[] {
  if (!columnKey || !order) return [...rows];
  const dir = order === "ascend" ? 1 : -1;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    let cmp = 0;
    if (columnKey === "lateDailySessions") {
      cmp = a.lateDailySessions - b.lateDailySessions;
    } else if (columnKey === "userName") {
      cmp = a.userName.localeCompare(b.userName);
    }
    return dir * cmp;
  });
  return sorted;
}

interface TimeTrackingStatistics {
  users: UserTimeTracking[];
  totalUsers: number;
  totalSessions: number;
  longSessionsCount: number;
  lateDailySessionsCount?: number;
}

interface TimeTrackingStatisticsPageProps {
  /** API endpoint (mặc định dùng public để không cần auth). */
  endpoint?: string;
  /** Lưu thứ tự kéo thả & ẩn/hiện thành viên vào localStorage (dùng cho trang public). */
  persistMemberLayout?: boolean;
}

const TimeTrackingStatisticsPage: React.FC<TimeTrackingStatisticsPageProps> = ({
  endpoint: propEndpoint,
  persistMemberLayout = false,
}) => {
  const endpoint = propEndpoint ?? `/api/public/time-tracking/statistics`;

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [statisticsRefreshKey, setStatisticsRefreshKey] = useState(0);
  const [data, setData] = useState<UserTimeTracking[]>([]);
  const [statistics, setStatistics] = useState<TimeTrackingStatistics | null>(null);
  const [detailUser, setDetailUser] = useState<UserTimeTracking | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const currentDate = new Date();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const adjustedfirstDayOfMonth = formatDateWithTimezoneOffset(
    firstDayOfMonth,
    7
  );
  const adjustedlastDayOfMonth = formatDateWithTimezoneOffset(
    lastDayOfMonth,
    7
  );
  const [startDate, setStartDate] = useState<string | null>(
    adjustedfirstDayOfMonth
  );
  const [endDate, setEndDate] = useState<string | null>(adjustedlastDayOfMonth);

  const [hiddenMemberKeysHours, setHiddenMemberKeysHours] = useState<Set<string>>(() =>
    persistMemberLayout ? loadHiddenMembersFromStorage(LS_MEMBER_HIDDEN_HOURS) : new Set()
  );
  const [hiddenMemberKeysLateDaily, setHiddenMemberKeysLateDaily] = useState<Set<string>>(() =>
    persistMemberLayout ? loadHiddenMembersFromStorage(LS_MEMBER_HIDDEN_LATE_DAILY) : new Set()
  );
  const [activeTabKey, setActiveTabKey] = useState<"hours" | "late-daily">("hours");

  /** Thứ tự shared lấy từ server (single source of truth khi user chưa drag-drop local). */
  const [serverOrder, setServerOrder] = useState<string[]>([]);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  /** Map normalizedKey → position number người dùng nhập trong modal. 0/null = không xếp. */
  const [orderInputs, setOrderInputs] = useState<Map<string, number | null>>(
    new Map()
  );
  const [orderSaving, setOrderSaving] = useState(false);
  /** Danh sách nhân viên từ Tiktoker, kể cả người chưa có phiên trong khoảng xem. */
  const [workSessionUsers, setWorkSessionUsers] = useState<WorkSessionUser[]>([]);
  const [loadingWorkSessionUsers, setLoadingWorkSessionUsers] = useState(false);

  const [hoursSort, setHoursSort] = useState<{
    columnKey: string | null;
    order: ColumnSortOrder;
  }>({ columnKey: null, order: null });

  const [lateDailySort, setLateDailySort] = useState<{
    columnKey: string | null;
    order: ColumnSortOrder;
  }>({ columnKey: null, order: null });

  const LONG_SESSION_THRESHOLD = 4 * 3600 + 20 * 60; // 4h20p = 15600 giây

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return '0m';
    }
  };

  const formatDurationToTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatDateTime = (dateString: string): string => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const normalizeUserName = (name: string): string =>
    name.trim().toLowerCase().replace(/\s+/g, " ");

  const sortKeysDefault = useCallback((keys: string[]) => {
    return [...keys].sort((a, b) => a.localeCompare(b));
  }, []);

  /**
   * userByKey gồm:
   *  - User có entry trong range (từ API stats)
   *  - Placeholder cho user có trong server order nhưng chưa có entry — để vẫn
   *    hiển thị trong bảng với "0h 0m" (yêu cầu user 2026-06-01).
   */
  const userByKey = useMemo(() => {
    const m = new Map<string, UserTimeTracking>();
    const fuzzyTaken = new Set<string>();
    data.forEach((user) => {
      const k = normalizeUserName(user.userName);
      m.set(k, user);
      fuzzyTaken.add(fuzzyMemberKey(user.userName));
    });
    if (persistMemberLayout) {
      serverOrder.forEach((rawName) => {
        const fuzzy = fuzzyMemberKey(rawName);
        if (fuzzyTaken.has(fuzzy)) return; // đã có entry, skip
        const key = normalizeUserName(rawName);
        if (m.has(key)) return;
        m.set(key, makePlaceholderUser(rawName));
        fuzzyTaken.add(fuzzy);
      });
    }
    return m;
  }, [data, persistMemberLayout, serverOrder]);

  const dataKeysSig = useMemo(() => Array.from(userByKey.keys()).sort().join("|"), [userByKey]);

  /** Map fuzzy key → position từ server order (admin set qua modal). */
  const effectiveDefaultPositions = useMemo(
    () => buildFuzzyPositionMap(serverOrder),
    [serverOrder]
  );

  /**
   * Server order là source duy nhất khi persistMemberLayout=true.
   * Chỉ user có position trong server order mới hiển thị.
   */
  const orderedDataKeys = useMemo(() => {
    const keys = Array.from(userByKey.keys());
    if (!persistMemberLayout) {
      return sortKeysDefault(keys);
    }
    const positioned: Array<{ k: string; pos: number }> = [];
    for (const k of keys) {
      const user = userByKey.get(k);
      if (!user) continue;
      const fuzzy = fuzzyMemberKey(user.userName);
      const pos = effectiveDefaultPositions.get(fuzzy);
      if (pos === undefined) continue; // không set → ẩn
      positioned.push({ k, pos });
    }
    positioned.sort((a, b) => a.pos - b.pos);
    return positioned.map((p) => p.k);
  }, [userByKey, persistMemberLayout, effectiveDefaultPositions, sortKeysDefault]);

  // Fetch server-saved order on mount (chỉ khi trang public có persistMemberLayout).
  useEffect(() => {
    if (!persistMemberLayout) return;
    let cancelled = false;
    axios
      .get<{ order: string[] }>(MEMBER_ORDER_ENDPOINT)
      .then((res) => {
        if (cancelled) return;
        const order = Array.isArray(res.data?.order) ? res.data.order : [];
        setServerOrder(order);
      })
      .catch((err) => {
        console.error("Failed to fetch member order:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [persistMemberLayout]);

  const visibleDataKeysOrderedHours = useMemo(
    () => orderedDataKeys.filter((k) => !hiddenMemberKeysHours.has(k)),
    [orderedDataKeys, hiddenMemberKeysHours]
  );

  const visibleDataKeysOrderedLateDaily = useMemo(
    () => orderedDataKeys.filter((k) => !hiddenMemberKeysLateDaily.has(k)),
    [orderedDataKeys, hiddenMemberKeysLateDaily]
  );

  const usersOrderedHours = useMemo(
    () =>
      visibleDataKeysOrderedHours
        .map((k) => userByKey.get(k))
        .filter(Boolean) as UserTimeTracking[],
    [visibleDataKeysOrderedHours, userByKey]
  );

  const usersOrderedLateDaily = useMemo(
    () =>
      visibleDataKeysOrderedLateDaily
        .map((k) => userByKey.get(k))
        .filter(Boolean) as UserTimeTracking[],
    [visibleDataKeysOrderedLateDaily, userByKey]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await axios.get(`${endpoint}?${params.toString()}`);
        const stats: TimeTrackingStatistics = response.data;

        // Khử trùng user theo tên đã normalize để tránh 1 user có 2 dòng (1 dòng 0, 1 dòng có số liệu)
        const userMap = new Map<string, UserTimeTracking>();
        stats.users.forEach((user) => {
          const normalizedName = normalizeUserName(user.userName);
          const existing = userMap.get(normalizedName);
          if (!existing) {
            userMap.set(normalizedName, user);
            return;
          }

          const existingScore = (existing.totalDuration || 0) + (existing.totalSessions || 0);
          const nextScore = (user.totalDuration || 0) + (user.totalSessions || 0);
          if (nextScore > existingScore || (!existing.userId && !!user.userId)) {
            userMap.set(normalizedName, user);
          }
        });

        const allUsers: UserTimeTracking[] = Array.from(userMap.values());

        setStatistics(stats);
        setData(allUsers);
      } catch (error) {
        console.error('Error fetching time tracking statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, endpoint, statisticsRefreshKey]);

  const handleRefreshWorkSessions = useCallback(() => {
    setStatisticsRefreshKey((key) => key + 1);
    message.success("Đã tải lại dữ liệu từ Tiktoker");
  }, []);

  const filteredHoursData = useMemo(() => {
    if (!searchQuery.trim()) return usersOrderedHours;
    const q = searchQuery.trim().toLowerCase();
    return usersOrderedHours.filter(
      (user) =>
        user.userName.toLowerCase().includes(q) ||
        user.userId.toLowerCase().includes(q)
    );
  }, [usersOrderedHours, searchQuery]);

  const filteredLateDailyData = useMemo(() => {
    if (!searchQuery.trim()) return usersOrderedLateDaily;
    const q = searchQuery.trim().toLowerCase();
    return usersOrderedLateDaily.filter(
      (user) =>
        user.userName.toLowerCase().includes(q) ||
        user.userId.toLowerCase().includes(q)
    );
  }, [usersOrderedLateDaily, searchQuery]);

  const hoursDisplayOrdered = useMemo(
    () => applyHoursColumnSort(filteredHoursData, hoursSort.columnKey, hoursSort.order),
    [filteredHoursData, hoursSort]
  );

  const handleHoursTableChange = useCallback<
    NonNullable<TableProps<UserTimeTracking>["onChange"]>
  >((_pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s || !s.order) {
      setHoursSort({ columnKey: null, order: null });
      return;
    }
    const field = String(s.field ?? s.columnKey ?? "");
    setHoursSort({ columnKey: field, order: s.order });
  }, []);

  const handleLateDailyTableChange = useCallback<
    NonNullable<TableProps<LateDailyRow>["onChange"]>
  >((_pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s || !s.order) {
      setLateDailySort({ columnKey: null, order: null });
      return;
    }
    const field = String(s.field ?? s.columnKey ?? "");
    setLateDailySort({ columnKey: field, order: s.order });
  }, []);

  const allMemberKeysSorted = useMemo(
    () => sortKeysDefault(Array.from(userByKey.keys())),
    [userByKey, sortKeysDefault]
  );

  const memberVisibilityPopoverContentHours = persistMemberLayout ? (
    <div style={{ maxWidth: 360, maxHeight: 420, overflowY: "auto" }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
        Bỏ chọn để ẩn thành viên khỏi tab Thống kê giờ làm. Cài đặt được lưu trên trình duyệt này.
      </Typography.Paragraph>
      <Checkbox.Group
        style={{ width: "100%" }}
        value={allMemberKeysSorted.filter((k) => !hiddenMemberKeysHours.has(k))}
        onChange={(checked) => {
          const checkedSet = new Set(checked as string[]);
          const nextHidden = new Set(allMemberKeysSorted.filter((k) => !checkedSet.has(k)));
          setHiddenMemberKeysHours(nextHidden);
          saveHiddenMembersToStorage(LS_MEMBER_HIDDEN_HOURS, nextHidden);
        }}
      >
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {allMemberKeysSorted.map((k) => {
            const user = userByKey.get(k);
            const label = user?.userName ?? k;
            return (
              <Checkbox key={k} value={k}>
                {label}
              </Checkbox>
            );
          })}
        </Space>
      </Checkbox.Group>
    </div>
  ) : null;

  const memberVisibilityPopoverContentLateDaily = persistMemberLayout ? (
    <div style={{ maxWidth: 360, maxHeight: 420, overflowY: "auto" }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
        Bỏ chọn để ẩn thành viên khỏi tab Thống kê trễ daily. Cài đặt được lưu trên trình duyệt này.
      </Typography.Paragraph>
      <Checkbox.Group
        style={{ width: "100%" }}
        value={allMemberKeysSorted.filter((k) => !hiddenMemberKeysLateDaily.has(k))}
        onChange={(checked) => {
          const checkedSet = new Set(checked as string[]);
          const nextHidden = new Set(allMemberKeysSorted.filter((k) => !checkedSet.has(k)));
          setHiddenMemberKeysLateDaily(nextHidden);
          saveHiddenMembersToStorage(LS_MEMBER_HIDDEN_LATE_DAILY, nextHidden);
        }}
      >
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {allMemberKeysSorted.map((k) => {
            const user = userByKey.get(k);
            const label = user?.userName ?? k;
            return (
              <Checkbox key={k} value={k}>
                {label}
              </Checkbox>
            );
          })}
        </Space>
      </Checkbox.Group>
    </div>
  ) : null;

  const handleDateChange = useCallback((dates: [dayjs.Dayjs, dayjs.Dayjs] | null, dateStrings: [string, string]) => {
    if (dates && dateStrings) {
      setStartDate(dateStrings[0] ? formatDateWithTimezoneOffset(dates[0].toDate(), 7) : null);
      setEndDate(dateStrings[1] ? formatDateWithTimezoneOffset(dates[1].toDate(), 7) : null);
    }
  }, []);

  /**
   * Mở modal và lấy danh sách nhân viên từ Tiktoker.
   * init position từ serverOrder qua fuzzy match.
   */
  const openOrderModal = useCallback(async () => {
    setOrderModalOpen(true);
    setLoadingWorkSessionUsers(true);
    try {
      const res = await axios.get<{ users: WorkSessionUser[] }>(
        WORK_SESSION_USERS_ENDPOINT
      );
      const users = Array.isArray(res.data?.users) ? res.data.users : [];
      setWorkSessionUsers(users);

      const inputs = new Map<string, number | null>();
      for (const u of users) {
        const key = normalizeUserName(u.name);
        const fuzzy = fuzzyMemberKey(u.name);
        const pos = effectiveDefaultPositions.get(fuzzy);
        inputs.set(key, pos !== undefined ? pos + 1 : null);
      }
      setOrderInputs(inputs);
    } catch (err) {
      console.error("Failed to fetch work session users:", err);
      message.error("Lỗi tải danh sách thành viên từ Tiktoker");
      setWorkSessionUsers([]);
      setOrderInputs(new Map());
    } finally {
      setLoadingWorkSessionUsers(false);
    }
  }, [effectiveDefaultPositions]);

  const handleOrderInputChange = useCallback(
    (key: string, value: number | null) => {
      setOrderInputs((prev) => {
        const next = new Map(prev);
        next.set(
          key,
          typeof value === "number" && value > 0 ? Math.floor(value) : null
        );
        return next;
      });
    },
    []
  );

  /**
   * Gửi thứ tự lên server theo tên nhân viên canonical.
   */
  const saveOrderModal = useCallback(async () => {
    setOrderSaving(true);
    try {
      // normalizedKey → canonical user name
      const nameByKey = new Map<string, string>();
      for (const u of workSessionUsers) {
        nameByKey.set(normalizeUserName(u.name), u.name);
      }

      const entries: Array<{ pos: number; name: string }> = [];
      orderInputs.forEach((pos, key) => {
        const name = nameByKey.get(key);
        if (!name || pos == null || pos <= 0) return;
        entries.push({ pos, name });
      });
      entries.sort((a, b) =>
        a.pos !== b.pos ? a.pos - b.pos : a.name.localeCompare(b.name)
      );
      const order = entries.map((e) => e.name);
      const res = await axios.put<{ order: string[] }>(
        MEMBER_ORDER_ENDPOINT,
        { order }
      );
      setServerOrder(Array.isArray(res.data?.order) ? res.data.order : order);
      message.success("Đã lưu thứ tự hiển thị");
      setOrderModalOpen(false);
    } catch (err) {
      console.error("Failed to save member order:", err);
      message.error("Lưu thất bại");
    } finally {
      setOrderSaving(false);
    }
  }, [orderInputs, workSessionUsers]);

  /** Rows hiển thị trong modal, sắp xếp theo position. */
  const orderModalRows = useMemo(() => {
    if (!orderModalOpen) return [];
    const rows = workSessionUsers.map((u) => {
      const key = normalizeUserName(u.name);
      return {
        id: u.id,
        key,
        name: u.name,
        status: u.status,
        pos: orderInputs.get(key) ?? null,
      };
    });
    rows.sort((a, b) => {
      const ap = a.pos ?? Number.MAX_SAFE_INTEGER;
      const bp = b.pos ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [orderModalOpen, workSessionUsers, orderInputs]);

  const orderModalColumns: ColumnProps<{
    id: string;
    key: string;
    name: string;
    status: string;
    pos: number | null;
  }>[] = [
    {
      title: "Vị trí",
      dataIndex: "pos",
      key: "pos",
      width: 90,
      render: (pos: number | null, record) => (
        <InputNumber
          value={pos ?? undefined}
          min={1}
          max={9999}
          onChange={(v) =>
            handleOrderInputChange(record.key, typeof v === "number" ? v : null)
          }
          style={{ width: "100%" }}
          placeholder="—"
        />
      ),
    },
    {
      title: "Thành viên",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status: string) => (
        <Tag color={status === "ACTIVE" ? "green" : "default"}>{status}</Tag>
      ),
    },
  ];

  const sessionColumns: ColumnProps<TimeTrackingSession>[] = [
    {
      title: 'Bắt đầu',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (text: string) => formatDateTime(text),
      width: 150,
    },
    {
      title: 'Kết thúc',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (text: string | null) => (text ? formatDateTime(text) : '-'),
      width: 150,
    },
    {
      title: 'Thời gian',
      dataIndex: 'durationFormatted',
      key: 'durationFormatted',
      render: (text: string, record: TimeTrackingSession) => (
        <span style={{
          color: record.isLongSession ? '#ff4d4f' : (record.isActive ? '#52c41a' : 'inherit'),
          fontWeight: record.isLongSession || record.isActive ? 'bold' : 'normal'
        }}>
          {text}
          {record.isLongSession && !record.isActive && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              {'>'} 4h20p
            </Tag>
          )}
          {record.isLateDaily && !record.isActive && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              Trễ daily
            </Tag>
          )}
        </span>
      ),
      width: 180,
    },
    {
      title: 'Onboard',
      dataIndex: 'onboardFormatted',
      key: 'onboardFormatted',
      render: (text: string) => text || '0h 0m',
      width: 100,
    },
    {
      title: 'OT',
      dataIndex: 'otFormatted',
      key: 'otFormatted',
      render: (text: string) => text || '0h 0m',
      width: 100,
    },
  ];

  const dailySessionGroups = useMemo((): DailySessionGroup[] => {
    if (!detailUser) return [];
    const groupMap = new Map<string, DailySessionGroup>();
    (detailUser.sessions || []).forEach((session) => {
      const dayKey = dayjs(session.startTime).format("YYYY-MM-DD");
      const dayLabel = dayjs(session.startTime).format("DD/MM/YYYY");
      const current = groupMap.get(dayKey);
      if (!current) {
        groupMap.set(dayKey, {
          dayKey,
          dayLabel,
          sessions: [session],
          totalDuration: session.durationSeconds ?? 0,
          onboardDuration: session.onboardSeconds ?? 0,
          otDuration: session.otSeconds ?? 0,
          longSessions: session.isLongSession ? 1 : 0,
          lateDailySessions: session.isLateDaily ? 1 : 0,
        });
        return;
      }
      current.sessions.push(session);
      current.totalDuration += session.durationSeconds ?? 0;
      current.onboardDuration += session.onboardSeconds ?? 0;
      current.otDuration += session.otSeconds ?? 0;
      current.longSessions += session.isLongSession ? 1 : 0;
      current.lateDailySessions += session.isLateDaily ? 1 : 0;
    });

    return Array.from(groupMap.values())
      .map((group) => ({
        ...group,
        sessions: [...group.sessions].sort((a, b) => {
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
        }),
      }))
      .sort((a, b) => dayjs(b.dayKey).valueOf() - dayjs(a.dayKey).valueOf());
  }, [detailUser]);

  const dailyGroupColumns: ColumnProps<DailySessionGroup>[] = [
    {
      title: "Ngày",
      dataIndex: "dayLabel",
      key: "dayLabel",
      width: 140,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Số ca",
      dataIndex: "sessions",
      key: "sessionCount",
      width: 90,
      render: (sessions: TimeTrackingSession[]) => sessions.length,
    },
    {
      title: "Tổng giờ",
      dataIndex: "totalDuration",
      key: "totalDuration",
      render: (value: number) => formatDurationToTime(value),
    },
    {
      title: "Onboard",
      dataIndex: "onboardDuration",
      key: "onboardDuration",
      render: (value: number) => formatDurationToTime(value),
    },
    {
      title: "OT",
      dataIndex: "otDuration",
      key: "otDuration",
      render: (value: number) => formatDurationToTime(value),
    },
    {
      title: "Ca > 4h20p",
      dataIndex: "longSessions",
      key: "longSessions",
      render: (value: number) => (value > 0 ? <Tag color="red">{value}</Tag> : 0),
      width: 110,
    },
    {
      title: "Trễ daily",
      dataIndex: "lateDailySessions",
      key: "lateDailySessions",
      render: (value: number) => (value > 0 ? <Tag color="orange">{value}</Tag> : 0),
      width: 110,
    },
  ];

  const userRowKey = useCallback(
    (record: UserTimeTracking) =>
      record.userId || `placeholder-${normalizeUserName(record.userName)}`,
    []
  );

  /**
   * Hours-tab columns. "Tổng giờ làm" và "Onboard" có sort (click header để asc/desc).
   * Các cột khác hiển thị thuần — thứ tự mặc định = server order.
   */
  const columns = useMemo((): ColumnProps<UserTimeTracking>[] => {
    return [
      {
        title: "STT",
        key: "index",
        width: 60,
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      {
        title: "Thành viên",
        dataIndex: "userName",
        key: "userName",
        render: (_: string, record: UserTimeTracking) => {
          return (
            <Tooltip title={record.userName}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontWeight: "bold" }}>{record.userName}</span>
                {record.isActive && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Đang làm việc
                  </Tag>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Tổng giờ làm",
        dataIndex: "totalDuration",
        key: "totalDuration",
        render: (_: unknown, record: UserTimeTracking) => (
          <span style={{ fontWeight: "bold" }}>{formatDurationToTime(record.totalDuration)}</span>
        ),
        sorter: (a: UserTimeTracking, b: UserTimeTracking) => a.totalDuration - b.totalDuration,
        sortOrder:
          hoursSort.columnKey === "totalDuration" ? hoursSort.order ?? undefined : undefined,
      },
      {
        title: "Onboard (8h-17h30)",
        dataIndex: "onboardDuration",
        key: "onboardDuration",
        render: (_: unknown, record: UserTimeTracking) =>
          formatDurationToTime(record.onboardDuration ?? 0),
        sorter: (a: UserTimeTracking, b: UserTimeTracking) =>
          (a.onboardDuration ?? 0) - (b.onboardDuration ?? 0),
        sortOrder:
          hoursSort.columnKey === "onboardDuration" ? hoursSort.order ?? undefined : undefined,
      },
      {
        title: "OT",
        dataIndex: "otDuration",
        key: "otDuration",
        render: (_: unknown, record: UserTimeTracking) =>
          formatDurationToTime(record.otDuration ?? 0),
      },
      {
        title: "Ca > 4h20p",
        dataIndex: "longSessions",
        key: "longSessions",
        render: (value: number) =>
          value > 0 ? <Tag color="red">{value}</Tag> : <span>0</span>,
      },
      {
        title: "Chi tiết",
        key: "details",
        width: 110,
        render: (_: unknown, record: UserTimeTracking) => (
          <Button
            size="small"
            onClick={() => {
              setDetailUser(record);
              setDetailModalOpen(true);
            }}
            icon={<EyeOutlined />}
          />
        ),
      },
    ];
  }, [hoursSort, setDetailUser, setDetailModalOpen]);

  const lateDailyBase = useMemo((): LateDailyRow[] => {
    return filteredLateDailyData
      .map((u) => ({
        ...u,
        lateDailySessions: u.lateDailySessions ?? 0,
        lateSessions: (u.sessions || []).filter((s) => s.isLateDaily),
      }))
      .filter((u) => u.lateDailySessions > 0);
  }, [filteredLateDailyData]);

  const lateDailyDisplayOrdered = useMemo(
    () =>
      applyLateDailyColumnSort(
        lateDailyBase,
        lateDailySort.columnKey,
        lateDailySort.order
      ),
    [lateDailyBase, lateDailySort]
  );

  const chartDataHours = useMemo(() => {
    const ordered = hoursDisplayOrdered;
    return {
      labels: ordered.map((u) => u.userName),
      datasets: [
        {
          label: "Onboard (8h-17h30)",
          data: ordered.map((u) => Math.round(((u.onboardDuration ?? 0) / 3600) * 10) / 10),
          backgroundColor: "rgba(24, 144, 255, 0.8)",
          borderColor: "rgba(24, 144, 255, 1)",
          borderWidth: 1,
        },
        {
          label: "OT",
          data: ordered.map((u) => Math.round(((u.otDuration ?? 0) / 3600) * 10) / 10),
          backgroundColor: "rgba(250, 140, 22, 0.8)",
          borderColor: "rgba(250, 140, 22, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [hoursDisplayOrdered]);

  const chartDataLateDaily = useMemo(() => {
    const ordered = lateDailyDisplayOrdered;
    return {
      labels: ordered.map((u) => u.userName),
      datasets: [
        {
          label: "Số lần trễ daily",
          data: ordered.map((u) => u.lateDailySessions ?? 0),
          backgroundColor: ordered.map(() => "rgba(250, 140, 22, 0.8)"),
          borderColor: "rgba(250, 140, 22, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [lateDailyDisplayOrdered]);

  const handleExportExcel = useCallback(() => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Thống kê giờ làm
    const hoursData = hoursDisplayOrdered.map((user, index) => ({
      'STT': index + 1,
      'Thành viên': user.userName,
      'Tổng giờ làm': formatDurationToTime(user.totalDuration),
      'Onboard (8h-17h30)': formatDurationToTime(user.onboardDuration ?? 0),
      'OT': formatDurationToTime(user.otDuration ?? 0),
      'Ca > 4h20p': user.longSessions,
    }));
    const hoursWorksheet = XLSX.utils.json_to_sheet(hoursData);
    XLSX.utils.book_append_sheet(workbook, hoursWorksheet, 'Thống kê giờ làm');

    // Sheet 2: Thống kê trễ daily
    const lateDailyExportData = lateDailyDisplayOrdered.map((user, index) => ({
      'STT': index + 1,
      'Thành viên': user.userName,
      'Số lần trễ daily': user.lateDailySessions || 0,
    }));
    const lateDailyWorksheet = XLSX.utils.json_to_sheet(lateDailyExportData);
    XLSX.utils.book_append_sheet(workbook, lateDailyWorksheet, 'Thống kê trễ daily');

    // Xuất file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const dateRange = startDate && endDate 
      ? `${dayjs(startDate).format('DD-MM-YYYY')}_${dayjs(endDate).format('DD-MM-YYYY')}`
      : dayjs().format('DD-MM-YYYY');
    const filename = `Time_Tracking_${dateRange}.xlsx`;
    saveAs(blob, filename);
  }, [hoursDisplayOrdered, lateDailyDisplayOrdered, startDate, endDate]);

  const lateDailyColumns = useMemo((): ColumnProps<LateDailyRow>[] => {
    return [
      {
        title: "Thành viên",
        dataIndex: "userName",
        key: "userName",
        sorter: (a: LateDailyRow, b: LateDailyRow) => a.userName.localeCompare(b.userName),
        sortOrder:
          lateDailySort.columnKey === "userName" ? lateDailySort.order ?? undefined : undefined,
        render: (_: string, record: LateDailyRow, index: number) => (
          <span style={{ fontWeight: index < 3 ? "bold" : "normal" }}>{record.userName}</span>
        ),
      },
      {
        title: "Số lần trễ daily",
        dataIndex: "lateDailySessions",
        key: "lateDailySessions",
        render: (value: number) => <Tag color="orange">{value}</Tag>,
        sorter: (a: LateDailyRow, b: LateDailyRow) => a.lateDailySessions - b.lateDailySessions,
        sortOrder:
          lateDailySort.columnKey === "lateDailySessions"
            ? lateDailySort.order ?? undefined
            : undefined,
      },
    ];
  }, [lateDailySort]);

  const expandedLateDailyRender = (record: LateDailyRow) => {
    const cols: ColumnProps<TimeTrackingSession>[] = [
      {
        title: 'Bắt đầu',
        dataIndex: 'startTime',
        key: 'startTime',
        render: (t: string) => formatDateTime(t),
        width: 150,
      },
      {
        title: 'Kết thúc',
        dataIndex: 'endTime',
        key: 'endTime',
        render: (t: string | null) => (t ? formatDateTime(t) : '—'),
        width: 150,
      },
      {
        title: 'Thời gian',
        dataIndex: 'durationFormatted',
        key: 'durationFormatted',
        width: 100,
      },
    ];
    return (
      <Table
        columns={cols}
        dataSource={record.lateSessions}
        rowKey="sessionId"
        pagination={false}
        size="small"
      />
    );
  };

  return (
    <Fragment>
      <div style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}>
        {statistics && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Tổng thành viên"
                  value={statistics.totalUsers}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Tổng ca làm việc"
                  value={statistics.totalSessions}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Ca > 4h20p"
                  value={statistics.longSessionsCount}
                  valueStyle={{ color: statistics.longSessionsCount > 0 ? '#ff4d4f' : undefined }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Trễ daily"
                  value={statistics.lateDailySessionsCount ?? 0}
                  valueStyle={{ color: (statistics.lateDailySessionsCount ?? 0) > 0 ? '#fa8c16' : undefined }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Search
            placeholder="Tìm kiếm theo tên..."
            allowClear
            style={{ width: 300 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <StableDatePicker
            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            placeholder={['Từ ngày', 'Đến ngày']}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
          >
            Xuất Excel
          </Button>
          <Tooltip title="Tải lại dữ liệu phiên làm việc từ Tiktoker">
            <Button
              icon={<SyncOutlined />}
              onClick={handleRefreshWorkSessions}
            >
              Tải lại
            </Button>
          </Tooltip>
          {persistMemberLayout &&
            (activeTabKey === "hours"
              ? memberVisibilityPopoverContentHours
              : memberVisibilityPopoverContentLateDaily) && (
            <Popover
              title={
                activeTabKey === "hours"
                  ? "Hiển thị thành viên (Giờ làm)"
                  : "Hiển thị thành viên (Trễ daily)"
              }
              trigger="click"
              placement="bottomLeft"
              content={
                activeTabKey === "hours"
                  ? memberVisibilityPopoverContentHours
                  : memberVisibilityPopoverContentLateDaily
              }
            >
              <Button icon={<TeamOutlined />}>Thành viên</Button>
            </Popover>
          )}
          {persistMemberLayout && (
            <Button
              icon={<OrderedListOutlined />}
              onClick={openOrderModal}
            >
              Quản lý thứ tự
            </Button>
          )}
        </div>

        <Tabs
          activeKey={activeTabKey}
          onChange={(key) => setActiveTabKey(key as "hours" | "late-daily")}
          items={[
            {
              key: 'hours',
              label: 'Thống kê giờ làm',
              children: (
                <>
                  <Card title="Biểu đồ tổng giờ làm (Onboard & OT)" size="small" style={{ marginBottom: 24 }}>
                    <div style={{ height: 320 }}>
                      <Bar
                        data={chartDataHours}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: true, position: 'top' },
                          },
                          scales: {
                            y: { beginAtZero: true, title: { display: true, text: "Giờ" } },
                            x: { title: { display: true, text: "Thành viên" } },
                          },
                        }}
                      />
                    </div>
                  </Card>
                  <Table
                    columns={columns}
                    dataSource={hoursDisplayOrdered}
                    loading={loading}
                    rowKey={userRowKey}
                    onChange={handleHoursTableChange}
                    pagination={false}
                  />
                </>
              ),
            },
            {
              key: 'late-daily',
              label: 'Thống kê trễ daily',
              children: (
                <>
                  <Card title="Biểu đồ số lần trễ daily (top 20)" size="small" style={{ marginBottom: 24 }}>
                    <div style={{ height: 320 }}>
                      <Bar
                        data={chartDataLateDaily}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: "Số lần" },
                              ticks: { stepSize: 1, precision: 0 },
                            },
                            x: { title: { display: true, text: "Thành viên" } },
                          },
                        }}
                      />
                    </div>
                  </Card>
                  <Table
                    columns={lateDailyColumns}
                    dataSource={lateDailyDisplayOrdered}
                    loading={loading}
                    rowKey={(record) => record.userId || `placeholder-${normalizeUserName(record.userName)}`}
                    onChange={handleLateDailyTableChange}
                    expandable={{
                      expandedRowRender: expandedLateDailyRender,
                      rowExpandable: (record) => (record.lateSessions?.length ?? 0) > 0,
                      expandIconColumnIndex: lateDailyColumns.length,
                    }}
                    pagination={false}
                  />
                </>
              ),
            },
          ]}
        />
        <Modal
          title={`Chi tiết ca làm việc${detailUser ? ` - ${detailUser.userName}` : ""}`}
          open={detailModalOpen}
          onCancel={() => setDetailModalOpen(false)}
          footer={null}
          width={1200}
          destroyOnClose
        >
          <Table
            columns={dailyGroupColumns}
            dataSource={dailySessionGroups}
            rowKey="dayKey"
            pagination={false}
            expandable={{
              expandedRowRender: (group) => (
                <Table
                  columns={sessionColumns}
                  dataSource={group.sessions}
                  rowKey="sessionId"
                  pagination={false}
                  size="small"
                />
              ),
              rowExpandable: (group) => group.sessions.length > 0,
              expandRowByClick: true,
              showExpandColumn: false,
            }}
          />
        </Modal>

        <Modal
          title="Quản lý thứ tự hiển thị mặc định"
          open={orderModalOpen}
          onCancel={() => setOrderModalOpen(false)}
          onOk={saveOrderModal}
          okText="Lưu"
          cancelText="Hủy"
          confirmLoading={orderSaving}
          okButtonProps={{ disabled: loadingWorkSessionUsers }}
          width={620}
          destroyOnClose
        >
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Danh sách lấy từ Tiktoker (bao gồm cả thành viên chưa có phiên).
            Số càng nhỏ hiển thị càng trước. Bỏ trống = ẩn khỏi bảng.
            Thay đổi áp dụng cho mọi người vào trang lần sau (trừ ai đã kéo-thả
            tự đặt thứ tự riêng).
          </Typography.Paragraph>
          <Table
            columns={orderModalColumns}
            dataSource={orderModalRows}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ y: 420 }}
            loading={loadingWorkSessionUsers}
            locale={{
              emptyText: loadingWorkSessionUsers
                ? "Đang tải..."
                : "Không lấy được danh sách nhân viên",
            }}
          />
        </Modal>
      </div>
    </Fragment>
  );
};

export default TimeTrackingStatisticsPage;
